import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {
    MvsFormFieldOutputBaseComponent
} from 'features/core/shared/form/field/output/mvs-form-field-output-base.component';
import {DtoEnumInfo} from 'features/core/shared/dto/dto-enum-info.interface';

/**
 * Output field component for a provision FACTOR together with the Berechnungsart it is applied
 * under — "12 × Monatsbeiträge", "8,5 Promille", "0".
 *
 * ── WHY FACTOR AND BERECHNUNGSART ARE ONE COMPONENT
 *
 * Because a factor without its mode is not a number anyone can read: `12` means twelve monthly
 * premiums under MONTHLY_PREMIUM and twelve thousandths of the total-term base under PER_MILLE —
 * two values three orders of magnitude apart. Splitting them across two cells makes it possible to
 * read one without the other, which is the single most expensive misreading this page can produce.
 * They travel together, so they render together. Registered as an output field component on the
 * design gate's H5 auflage, for the same reason as {@link CpFormFieldOutputOriginComponent}.
 *
 * ── THE ZERO RULE (requirement §7.7)
 *
 * **Factor 0 is a statement, not a missing value.** The reference tenant builds "there is nothing
 * for this combination" as an explicit row with factor 0 in mode Monatsbeiträge. It is rendered as
 * "0" — never as an empty cell, never as a dash. Empty and zero say different things, and on a page
 * that tells an adviser what he earns, confusing them is a wrong answer, not a cosmetic flaw.
 * `null` is the genuinely absent value and is the only case that renders a dash.
 *
 * This is also why the class does not reuse `MvsFormFieldOutputAmountComponent`: its
 * `getFormattedNumber` branches on `if (this.value)`, which is falsy for 0, and it forces two
 * decimals — a factor of 12 would read "12,00" and a factor of 0 would arrive through the
 * else-branch. Both are wrong here.
 *
 * ── NOTHING IS COMPUTED
 *
 * The factor is displayed exactly as the endpoint sent it. No division by 1000 for Promille, no
 * multiplication by a premium, no rounding of the business value — the class formats a number for
 * German eyes and stops there (requirement §7.8).
 */
@Component({
    selector: 'cp-form-field-output-factor',
    templateUrl: './cp-form-field-output-factor.component.html',
    styleUrls: ['./cp-form-field-output-factor.component.scss'],
    standalone: false
})
export class CpFormFieldOutputFactorComponent
    extends MvsFormFieldOutputBaseComponent
    implements OnInit, OnDestroy, OnChanges {

    /**
     * The backend's `calculationModeInfo` block — "Monatsbeiträge" / "Promille" straight from the
     * `@UiEnumValueInfo` on `ProvisionMatrixCalcMode`, so the page shows Klartext and never an enum
     * name (requirement §6), also for constants appended later.
     */
    @Input() calculationModeInfo: DtoEnumInfo;

    /** The raw mode, kept as the fallback when the `Info` block did not come along. */
    @Input() calculationMode: string | number;

    /** The formatted factor — "0" stays "0", `null` becomes a dash. */
    formattedValue: string;

    /** German plain text of the Berechnungsart, empty when there is no factor to qualify. */
    modeLabel: string;

    /** True for a factor of exactly 0 — drives the muted styling and the explanatory tooltip. */
    zeroFactor: boolean;

    /** True when there is no factor at all. */
    missing: boolean;

    /** Tooltip; carries the "0 heißt keine Vergütung" sentence where it matters. */
    tooltip: string;

    override ngOnInit() {
        super.ngOnInit();
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }
        this.refreshComponent();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
    }

    override refreshComponent(): void {

        this.missing = this.value == null;
        this.zeroFactor = !this.missing && Number(this.value) === 0;

        if (this.missing) {
            this.formattedValue = '—';
            this.modeLabel = '';
            this.tooltip = 'Für diese Zeile ist kein Faktor hinterlegt.';
            return;
        }

        this.formattedValue = this.formatFactor(Number(this.value));
        this.modeLabel = this.resolveModeLabel();

        this.tooltip = this.zeroFactor
            ? 'Faktor 0 — für diese Kombination gibt es ausdrücklich keine Vergütung. '
            + 'Das ist eine bewusste Angabe der Konfiguration, kein fehlender Wert.'
            : this.formattedValue + (this.modeLabel ? ' · ' + this.modeLabel : '');
    }

    /**
     * German number format with a VARIABLE number of decimals: a factor of 12 reads "12", one of
     * 8.5 reads "8,5", one of 0 reads "0".
     *
     * Fixed two decimals would be wrong in both directions here — it would dress an integer count
     * of monthly premiums as a decimal amount, and it would cut a per-mille rate carrying three
     * places. `maximumFractionDigits: 4` is the ceiling, matching the scale the backend column
     * carries; the minimum stays 0 so nothing is padded.
     */
    protected formatFactor(factor: number): string {
        return factor.toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 4});
    }

    /**
     * The Berechnungsart in plain German.
     *
     * Prefers the backend's label; falls back to the two known constants in BOTH wire forms (enum
     * name and ordinal — the convention this module's API DTOs carry). An unknown value yields an
     * empty label rather than a guess: a wrong mode beside a right factor is worse than no mode.
     */
    protected resolveModeLabel(): string {

        if (this.calculationModeInfo?.label) {
            return this.calculationModeInfo.label;
        }

        if (this.calculationMode === 'MONTHLY_PREMIUM' || this.calculationMode === 0) {
            return 'Monatsbeiträge';
        }
        if (this.calculationMode === 'PER_MILLE' || this.calculationMode === 1) {
            return 'Promille';
        }

        return '';
    }

}
